import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import Grid from '@material-ui/core/Grid/Grid';
import AmountInput from '@configuratorware/configurator-frontendgui/App/Shared/Components/AmountInput';
import isFunction from 'lodash/isFunction';
import get from 'lodash/get';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import upperFirst from 'lodash/upperFirst';
import ListItem from '@material-ui/core/ListItem/ListItem';
import Typography from '@material-ui/core/Typography/Typography';
import BulkNames from '@configuratorware/configurator-frontendgui/App/Shared/Containers/BulkNames';
import Image from '@configuratorware/configurator-frontendgui/App/Shared/Components/ProductVariant/Image';
import { sumBy } from 'lodash';

export const useStyles = makeStyles((theme) => ({
    ProductVariantRoot: {
        height: 'auto',
        alignItems: 'flex-start',
        paddingLeft: 0,
        [theme.breakpoints.down('xs')]: {
            paddingRight: 0,
            display: 'flex',
            flexDirection: 'column'
        },
    },
    ProductVariantImageItem: {
        width: 200,
        height: 100,
        marginRight: 50,
        maxWidth: 200,
        marginTop: 20,
    },
    productAmounts: {
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        [theme.breakpoints.down('xs')]: {
            display: 'flex',
            flexWrap: 'wrap',
        },
    },
    productAmountsSection: {
        display: 'flex',
        flexDirection: 'column',
    },
    TypographyBody1: {
        lineHeight: 2.5,
        display: 'flex',
        justifyContent: 'space-between',
    },
    ProductVariantImage: {
        width: '200px',
        height: '150px',
        maxWidth: 200,
        objectFit: 'contain',
    },
    title: {
        marginBottom: 12,
    },
    amountInput: {
        marginRight: 15
    },
    amountVariant: {
        width: 100,
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    gridFlowList: {
        position: 'relative',
        display: 'grid',
        width: '100%',
    },
}), {name: 'CustomProductVariant'});

export const hasError = (identifier, errors) => {
    if (!errors) {
        return false;
    }

    const error = find(errors.errors, function(o) {
        return o.itemIdentifier === identifier;
    });

    return !!error;
};

export interface ProductVariantProps {
    handleChange: (variant: string, size: string | null, value: number) => void;
    activeBulkNames: boolean;
    saveConfigurationBulkNames: (identifier: string, bulkNamesData: any) => void;
    amountData: {
        identifier: string,
        isDisabled: boolean,
        url: string,
        amount: number,
        title: string
    }[];
    error: any;
    title: string;
    image: string | null
}

const ProductVariant = (props: ProductVariantProps) => {
    const classes = useStyles();
    const [showBulkNameForm, setShowBulkNameForm] = useState(false);
    const [activeVariantItem, setActiveVariantItem] = useState({});
    const [activeSize, setActiveSize] = useState({});

    const handleChange = (variant, size) => value => {
        if (isFunction(props.handleChange)) {
            props.handleChange(variant, size, value);
        }
    };

    const handleFocus = event => {
        // select text async for mac os safari
        const target = event.target;
        setTimeout(() => target.select(), 0);
    };

    const handleBulkNameForm = (variant, size) => () => {
        const { activeBulkNames } = props;
        if (!activeBulkNames) {
            return;
        }
        setShowBulkNameForm(true);
        setActiveVariantItem(variant);
        setActiveSize(size || activeSize);
    };

    const handleBulkNamesFormClose = () => {
        setShowBulkNameForm(false);
    };

    const handleBulkNamesSave = (identifier, bulkNamesData) => {
        if (isFunction(props.saveConfigurationBulkNames)) {
            props.saveConfigurationBulkNames(identifier, bulkNamesData);
        }
    };

    const {
        amountData,
        error,
        activeBulkNames,
        title,
        image
    } = props;


    if (!amountData || amountData.length === 0) {
        return null;
    }
    const bulkNameVariant = isEmpty(activeSize) ? activeVariantItem : activeSize;

    return (
        <Grid container>
            <div className={classes.gridFlowList}>
                <ListItem
                    className={classes.ProductVariantRoot}
                >
                    <Grid
                        item
                        xs={4}
                        classes={{
                            item: classes.ProductVariantImageItem
                        }}
                    >
                        {image && <img src={image} alt={title} className={classes.ProductVariantImage} />}
                    </Grid>
                    <Grid item  className={classes.productAmountsSection}>
                        <Typography className={classes.title} variant="body2">{sumBy(amountData, 'amount') + ' x ' + title}</Typography>
                        <div className={classes.productAmounts}>
                            {amountData.map((variant, i) => {
                                if (variant.isDisabled === true) {
                                    return null;
                                }

                                return (
                                    <div className={classes.amountInput} key={i}>
                                        <Typography variant="body1" classes={{ root: classes.TypographyBody1 }}>
                                            <b> {upperFirst(variant.title)}</b>
                                        </Typography>
                                        <AmountInput
                                            value={variant.amount}
                                            hasValueLabel={true}
                                            amountLabel={t('productVariant.amountLabel')}
                                            onChange={handleChange(variant, null)}
                                            onClick={handleBulkNameForm(variant, null)}
                                            error={hasError(variant.identifier, error)}
                                            disabled={variant.isDisabled}
                                            readOnly={activeBulkNames}
                                            onChangeDebounceWait={0}
                                            rootProps={classes.amountVariant}
                                            onFocus={handleFocus}
                                            type="number"
                                            inputProps={{
                                              inputMode: "numeric",
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </Grid>
                </ListItem>              
                {showBulkNameForm && (
                    <BulkNames
                        selectedAmount={amountData}
                        selectedVariant={activeVariantItem}
                        activeSizeVariant={activeSize}
                        amountLabel={t('productVariant.amountLabel')}
                        handleChange={handleChange(bulkNameVariant, null)}
                        onClose={handleBulkNamesFormClose}
                        handleSave={handleBulkNamesSave}
                        showBulkNameForm={showBulkNameForm}
                    />
                )}
            </div>
        </Grid>
    );
}

export default ProductVariant;
